import { getCompanyTimezone } from "@repo/app-types/entities/company";
import {
  DEFAULT_CALLING_HOURS,
  DEFAULT_CALLING_RETRY_POLICY,
  type CallingHours,
  type CallingRetryPolicy,
} from "@repo/app-types/entities/teammateAgent";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import type { FC } from "react";
import { useUpdateTeammateAgentOutboundEnabledMutation } from "../../../api/entities/teammateAgent.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectCompany } from "../../company/companySlice.js";
import { OutboundCallsSection } from "./outboundCallsSection.js";
import { useStaticAgentConfig } from "./staticAgentConfigContext.js";

/**
 * Wires the outbound section to the two different save paths it straddles: the
 * calling hours and retry policy go through the config draft, while the on/off
 * switch writes straight through so a runaway agent can be stopped without
 * publishing whatever else is staged.
 */
export const OutboundCallsContainer: FC = () => {
  const { teammateAgentId, phoneNumber, agentConfig, isActionInProgress } =
    useStaticAgentConfig();

  const company = useAppSelector(selectCompany);

  const [updateOutboundEnabled, { isLoading: isUpdatingOutboundEnabled }] =
    useUpdateTeammateAgentOutboundEnabledMutation();

  const settings = agentConfig.data?.settings;
  const calling = settings?.calling;

  const handleOutboundEnabledChange = async (isEnabled: boolean) => {
    try {
      await updateOutboundEnabled({
        teammateAgentId,
        outboundEnabled: isEnabled,
      }).unwrap();
    } catch {
      showToast({
        title: isEnabled
          ? "Failed to enable outbound calling"
          : "Failed to disable outbound calling",
        variant: ToastVariant.Error,
      });
    }
  };

  const canEnableAgentManaged = useIsFeatureFlagEnabled(
    FeatureFlagKey.AgentManagedOutreach,
  );

  const handleCallingChange = (
    change: Partial<{
      hours: CallingHours;
      retry: CallingRetryPolicy;
      agent_managed: boolean;
    }>,
  ) => {
    if (!settings) {
      return;
    }

    agentConfig.onChange({
      settings: { ...settings, calling: { ...calling, ...change } },
    });
  };

  return (
    <OutboundCallsSection
      isOutboundEnabled={agentConfig.isOutboundEnabled}
      hours={calling?.hours ?? DEFAULT_CALLING_HOURS}
      retry={calling?.retry ?? DEFAULT_CALLING_RETRY_POLICY}
      // The agent's own zone wins server-side, so the label has to agree with
      // whatever `getCallingTimezone` will actually use.
      timezone={calling?.timezone ?? getCompanyTimezone(company)}
      callingHoursError={agentConfig.validationErrors.callingHours}
      hasPhoneNumber={phoneNumber !== null}
      isDisabled={
        isActionInProgress || isUpdatingOutboundEnabled || !agentConfig.data
      }
      onOutboundEnabledChange={(isEnabled) =>
        void handleOutboundEnabledChange(isEnabled)
      }
      onHoursChange={(hours) => handleCallingChange({ hours })}
      onRetryChange={(retry) => handleCallingChange({ retry })}
      isAgentManaged={calling?.agent_managed ?? false}
      canEnableAgentManaged={canEnableAgentManaged}
      onAgentManagedChange={(agent_managed) =>
        handleCallingChange({ agent_managed })
      }
      autoEnrollment={settings?.auto_enrollment}
      onAutoEnrollmentChange={(auto_enrollment) => {
        if (!settings) {
          return;
        }
        agentConfig.onChange({ settings: { ...settings, auto_enrollment } });
      }}
    />
  );
};
